import { useCallback, useEffect, useState } from 'react';

import { useLoadDexChart } from 'hooks/charts/useLoadDexChart';
import { IChartTimeframe } from 'types/charts';
import { durationToMs } from 'utils/duration';

interface IProps {
  timeframes: IChartTimeframe[];
  period: number;
}

const timeframeToRange = ({
  timeframe,
  candles,
  period,
}: {
  timeframe: string;
  candles: number;
  period: number;
}) => {
  const ms = durationToMs(timeframe);

  return {
    end: Date.now(),
    start: Date.now() - ms * candles + period,
  };
};

export const useDexCandlesByTimeframe = ({ timeframes, period }: IProps) => {
  const [resultCandles, setResultCandles] = useState<Record<string, number[][]>>({});

  const loadChart = useLoadDexChart();

  const setupChartTimeframes = useCallback(async () => {
    try {
      if (timeframes.length === 0) return;

      const ranges = timeframes.map(timeframe => {
        const tf = durationToMs(timeframe);

        const tfsCount = Math.ceil((24 * 60 * 60 * 1000) / tf);
        const limit = tfsCount + period;

        const range = timeframeToRange({ timeframe, candles: limit, period });

        return {
          timeframe,
          end: range.start / 1000,
          start: range.end / 1000,
          limit,
        };
      });

      const results = await Promise.all(
        ranges.map(el =>
          loadChart({
            timeframe: el.timeframe,
            startTime: el.start,
            endTime: el.end,
            isConverted: false,
            limit: el.limit,
          }),
        ),
      );

      const _candles = ranges.reduce(
        (acc, val, idx) => ({
          ...acc,
          [val.timeframe]: results[idx].candles,
        }),
        {},
      );

      setResultCandles(_candles);
    } catch (error) {
      console.log('error: ', error);
    }
  }, [timeframes, period, loadChart]);

  useEffect(() => {
    setupChartTimeframes();
  }, [setupChartTimeframes]);

  return {
    candles: resultCandles,
  };
};
